import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../hooks/useSocket';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { TableSkeleton } from '../../Components/common/Skeleton';
import Pagination from '../../Components/common/Pagination';
import {
  Search, SlidersHorizontal, Calendar, Phone, Stethoscope, Clock,
  ChevronDown, Loader2, RefreshCw, Plus, X, Trash2, CheckCircle2, Activity,
  Wallet, CalendarCheck
} from 'lucide-react';

const PAYMENT_STATUS_STYLES = {
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200',
  refunded: 'bg-blue-50 text-blue-700 border-blue-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200'
};

const PAYOUT_STATUS_STYLES = {
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  processing: 'bg-amber-50 text-amber-700 border-amber-200',
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  failed: 'bg-rose-50 text-rose-700 border-rose-200'
};

const STATUS_TABS = [
  { key: 'draft', label: 'Draft' },
  { key: 'pending_payment', label: 'Pending Payment' },
  { key: 'confirmed', label: 'Confirmed' },
  { key: 'completed', label: 'Completed' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'lost', label: 'Lost' },
  { key: 'overridden', label: 'Overridden' }
];

const STATUS_STYLES = {
  draft: 'bg-slate-100 text-slate-700 border-slate-200',
  pending_payment: 'bg-amber-50 text-amber-700 border-amber-200',
  confirmed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  completed: 'bg-blue-50 text-blue-700 border-blue-200',
  cancelled: 'bg-rose-50 text-rose-700 border-rose-200',
  lost: 'bg-orange-50 text-orange-700 border-orange-200',
  overridden: 'bg-purple-50 text-purple-700 border-purple-200'
};

const formatDateDisplay = (date) => {
  if (!date) return 'N/A';
  if (typeof date === 'string') {
    const trimmed = date.trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) return trimmed;
    const isoMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return 'N/A';
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  return `${day}-${month}-${year}`;
};

const formatTime12Hour = (time24) => {
  if (!time24 || typeof time24 !== 'string') return 'N/A';
  if (time24.includes('-')) {
    const parts = time24.split('-');
    return parts.map(p => formatTime12Hour(p.trim())).join(' - ');
  }
  const clean = time24.trim();
  if (clean.toLowerCase().includes('am') || clean.toLowerCase().includes('pm')) return clean;
  const [hours, minutes] = clean.split(':').map(Number);
  if (isNaN(hours) || isNaN(minutes)) return clean;
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${String(displayHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} ${period}`;
};

const AppointmentList = () => {
  const { user } = useAuth();
  const { socket } = useSocket() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('status') || '';
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: urlTab,
    search: '',
    fromDate: '',
    toDate: '',
    doctorId: ''
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [activeTab, setActiveTab] = useState(urlTab || 'all');

  useEffect(() => {
    setActiveTab(urlTab || 'all');
  }, [urlTab]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchTerm }));
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [filters, activeTab]);

  useEffect(() => {
    fetchAppointments();
  }, [pagination.page, filters, activeTab]);

  useEffect(() => {
    if (!socket) return;

    const handleRealTimeUpdate = () => {
      fetchAppointments();
    };

    socket.on('appointment_created', handleRealTimeUpdate);
    socket.on('appointment_updated', handleRealTimeUpdate);
    socket.on('lead_updated', handleRealTimeUpdate);
    socket.on('personal_notification', handleRealTimeUpdate);

    return () => {
      socket.off('appointment_created', handleRealTimeUpdate);
      socket.off('appointment_updated', handleRealTimeUpdate);
      socket.off('lead_updated', handleRealTimeUpdate);
      socket.off('personal_notification', handleRealTimeUpdate);
    };
  }, [socket]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  const fetchFilterOptions = async () => {
    try {
      const [docRes] = await Promise.all([
        api.get('/doctors?limit=100')
      ]);
      setDoctors(docRes.data.data.doctors || []);
    } catch (error) {
      console.error('Failed to load filter data');
    }
  };

  const fetchAppointments = async () => {
    try {
      if (appointments.length === 0) {
        setLoading(true);
      } else {
        setIsFetching(true);
      }
      const apiStatus = activeTab !== 'all' ? activeTab : '';
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        ...(apiStatus && { status: apiStatus }),
        ...(filters.search && { search: filters.search }),
        ...(filters.fromDate && { fromDate: filters.fromDate }),
        ...(filters.toDate && { toDate: filters.toDate }),
        ...(filters.doctorId && { doctorId: filters.doctorId })
      });

      const response = await api.get(`/appointments?${params.toString()}`);
      if (response.data?.success) {
        setAppointments(response.data.data.appointments || []);
        setPagination(prev => ({
          ...prev,
          total: response.data.data.total || 0,
          pages: response.data.data.pages || 0
        }));
      }
    } catch (error) {
      console.error('Failed to load appointments', error);
      toast.error(error.response?.data?.message || 'Failed to load appointments');
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    const nextParams = new URLSearchParams(searchParams);
    if (newTab === 'all') {
      nextParams.delete('status');
    } else {
      nextParams.set('status', newTab);
    }
    setSearchParams(nextParams);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    if (name === 'search') {
      setSearchTerm(value);
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      status: activeTab,
      search: '',
      fromDate: '',
      toDate: '',
      doctorId: ''
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const getStatusBadge = (status) => {
    const displayStatus = status || 'unknown';
    const style = STATUS_STYLES[status] || 'bg-gray-100 text-gray-800';
    return (
      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${style}`}>
        {displayStatus.replace('_', ' ')}
      </span>
    );
  };

  const canCreate = ['admin', 'customer_care', 'cc_head', 'doctor'].includes(user?.role);

  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/appointments/${deleteId}`);
      toast.success('Appointment deleted successfully');
      setDeleteId(null);
      fetchAppointments();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete appointment');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="h-10 w-40 bg-slate-200 animate-pulse rounded-2xl"></div>
        </div>
        <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-8">
          <TableSkeleton rows={8} />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-10">
      {/* Quick Switch Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white p-4 rounded-2xl border border-slate-200/80 shadow-2xs">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-slate-500">View:</span>
          <span className="px-3 py-1.5 bg-[#143E78] text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-2xs">
            All Appointments
          </span>
          <Link
            to="/appointments/follow-ups"
            className="px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all inline-flex items-center gap-1.5 cursor-pointer shadow-2xs"
          >
            <CalendarCheck size={13} className="text-blue-600" />
            <span>Follow-up Queue</span>
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 p-6 sm:p-8 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
          <div className="md:col-span-5 space-y-2">
            <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
              <Search size={14} className="text-[#143E78]" />
              Search Appointments
            </label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
              <input
                type="text"
                name="search"
                value={searchTerm}
                onChange={handleFilterChange}
                placeholder="Search by patient, phone, or booking ID..."
                className="w-full pl-11 pr-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
              />
            </div>
          </div>

          <div className="md:col-span-3">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`w-full px-4 py-3.5 rounded-2xl border-2 transition-all text-xs font-black uppercase tracking-widest flex items-center justify-center gap-2 ${showAdvanced
                ? 'bg-[#143E78] border-[#143E78] text-white shadow-lg shadow-[#143E78]/20'
                : 'bg-white border-slate-200 text-slate-600 hover:border-[#143E78] hover:text-[#143E78]'
                }`}
            >
              <SlidersHorizontal size={16} />
              {showAdvanced ? 'Hide Advanced' : 'Advanced'}
            </button>
          </div>

          <div className="md:col-span-4 space-y-2">
            <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
              <Activity size={14} className="text-[#143E78]" />
              Appointment Status
            </label>
            <div className="relative">
              <select
                value={activeTab}
                onChange={(e) => handleTabChange(e.target.value)}
                className="w-full pl-4 pr-11 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none"
              >
                <option value="all">All Appointments</option>
                {STATUS_TABS.map((t) => (
                  <option key={t.key} value={t.key}>{t.label}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvanced && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 pt-6 border-t-2 border-dashed border-slate-200">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Calendar size={14} className="text-[#143E78]" />
                From Date
              </label>
              <input
                type="date"
                name="fromDate"
                value={filters.fromDate}
                onChange={handleFilterChange}
                className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Calendar size={14} className="text-[#143E78]" />
                To Date
              </label>
              <input
                type="date"
                name="toDate"
                value={filters.toDate}
                onChange={handleFilterChange}
                className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none"
              />
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Stethoscope size={14} className="text-[#143E78]" />
                Select Doctor
              </label>
              <div className="relative">
                <select
                  name="doctorId"
                  value={filters.doctorId}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none"
                >
                  <option value="">All Doctors</option>
                  {doctors.map(d => (
                    <option key={d._id} value={d._id}>Dr. {d.user?.firstName} {d.user?.lastName}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-[11px] font-black text-slate-500 uppercase tracking-widest">
                <Activity size={14} className="text-[#143E78]" />
                Status
              </label>
              <div className="relative">
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-4 py-3.5 bg-slate-50 border-2 border-slate-200 rounded-2xl font-bold text-sm focus:border-[#143E78] focus:ring-4 focus:ring-[#143E78]/10 transition-all outline-none appearance-none"
                >
                  <option value="">All Statuses</option>
                  {STATUS_TABS.map((t) => (
                    <option key={t.key} value={t.key}>{t.label}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results Count */}
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
          Showing {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
        </p>
        {isFetching && (
          <div className="flex items-center gap-2 text-[10px] font-black text-[#143E78] uppercase tracking-widest">
            <Loader2 size={14} className="animate-spin" />
            Updating...
          </div>
        )}
      </div>

      {/* Appointments Content */}
      <div
        className={`bg-white rounded-3xl shadow-xl shadow-slate-200/50 border border-slate-100 overflow-hidden transition-all duration-300 ${isFetching ? 'opacity-60' : 'opacity-100'}`}
      >
        {/* Mobile Card View */}
        <div className="block lg:hidden divide-y divide-slate-100">
          {appointments.length > 0 ? (
            appointments.map((appointment) => (
              <div
                key={appointment._id}
                onClick={(e) => {
                  if (e.target.closest('a') || e.target.closest('button')) return;
                  navigate(`/appointments/${appointment._id}`);
                }}
                className="p-5 space-y-4 hover:bg-slate-50/50 transition-colors cursor-pointer"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-black text-slate-900 leading-tight truncate">{appointment.leadId?.patientName || appointment.patientName}</div>
                    <div className="text-xs font-bold text-slate-500 mt-0.5">{appointment.leadId?.patientPhone || appointment.patientPhone}</div>
                    {(appointment.leadId?.patientEmail || appointment.patientEmail) && (
                      <div className="text-[10px] font-bold text-slate-400 mt-0.5 truncate">{appointment.leadId?.patientEmail || appointment.patientEmail}</div>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      {appointment.leadId?.patientGender && (
                        <span className="text-[10px] font-bold text-slate-500 capitalize">{appointment.leadId.patientGender}</span>
                      )}
                      {appointment.leadId?.patientAge && (
                        <span className="text-[10px] font-bold text-slate-500">{appointment.leadId.patientAge} yrs</span>
                      )}
                    </div>
                  </div>
                  <div className="ml-3">
                    {getStatusBadge(appointment.status)}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Type</p>
                    <p className="text-xs font-black text-slate-900 capitalize">
                      {appointment.appointmentType === 'consultation' ? 'Online Consultation' : appointment.appointmentType?.replace('_', ' ')}
                    </p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-2xl border border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Scheduled</p>
                    <p className="text-xs font-black text-slate-900">
                      {formatDateDisplay(appointment.appointmentDate)}
                    </p>
                    <p className="text-[10px] font-bold text-slate-500 mt-0.5">{formatTime12Hour(appointment.appointmentTimeSlot)}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className="text-[9px] font-black text-[#143E78] font-mono">
                    #{appointment.bookingId || appointment._id.slice(-8).toUpperCase()}
                  </span>
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Platform Fee</p>
                  <p className="text-xs font-black text-slate-900">₹{appointment.platformFee ?? 0}</p>
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Final Price</p>
                  <p className="text-xs font-black text-[#143E78]">₹{appointment.finalPrice ?? '—'}</p>
                </div>

                <div className="pt-3 border-t border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Payment Status</p>
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${PAYMENT_STATUS_STYLES[appointment.paymentStatus] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                    {(appointment.paymentStatus || 'pending').replace(/_/g, ' ')}
                  </span>
                </div>

                {appointment.payoutStatus && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Payout Status</p>
                    <Link
                      to={`/payouts/${appointment.payoutId}`}
                      onClick={(e) => e.stopPropagation()}
                      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${PAYOUT_STATUS_STYLES[appointment.payoutStatus] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                    >
                      {appointment.payoutStatus.replace(/_/g, ' ')}
                    </Link>
                  </div>
                )}

                <div className="pt-3 border-t border-slate-100">
                  <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Doctor</p>
                  <div className="text-[11px] font-bold text-slate-700 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-[#143E78]"></span>
                    <span className="truncate">
                      {appointment.appointmentDoctorId?.user?.firstName ? `Dr. ${appointment.appointmentDoctorId.user.firstName} ${appointment.appointmentDoctorId.user.lastName}` : 'Not Assigned'}
                    </span>
                  </div>
                </div>

                {appointment.leadId?.comorbidities && (
                  <div className="pt-3 border-t border-slate-100">
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Comorbidities</p>
                    <p className="text-[11px] font-bold text-slate-700 line-clamp-2">{appointment.leadId.comorbidities}</p>
                  </div>
                )}

                <Link
                  to={`/appointments/${appointment._id}`}
                  className="block w-full text-center py-3.5 bg-[#143E78] text-white text-xs font-black uppercase tracking-wider rounded-2xl shadow-lg shadow-[#143E78]/20 hover:bg-[#0f2f5e] active:scale-[0.98] transition-all"
                >
                  View Full Details
                </Link>
              </div>
            ))
          ) : (
            <div className="p-16 text-center">
              <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mx-auto mb-4 border-2 border-dashed border-slate-200">
                <Calendar size={28} className="text-slate-300" />
              </div>
              <p className="text-sm font-black text-slate-500 uppercase tracking-wider">No appointments found</p>
              <p className="text-xs font-bold text-slate-400 mt-1">Try adjusting your filters or search criteria</p>
            </div>
          )}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100">
            <thead>
              <tr className="bg-slate-50/50">
                <th className="sticky left-0 bg-slate-50/95 z-10 px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest border-r border-slate-200 min-w-[180px]">
                  Patient
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[140px]">
                  Type
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[160px]">
                  Doctor
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[140px]">
                  Scheduled
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px]">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px]">
                  Final Price
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px]">
                  Payment Status
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px]">
                  Payout Status
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[120px]">
                  Platform Fee
                </th>
                <th className="px-6 py-4 text-left text-[10px] font-black text-slate-400 uppercase tracking-widest min-w-[100px]">
                  Booking ID
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {appointments.length > 0 ? (
                appointments.map((appointment) => (
                  <tr
                    key={appointment._id}
                    onClick={(e) => {
                      if (e.target.closest('a') || e.target.closest('button')) return;
                      navigate(`/appointments/${appointment._id}`);
                    }}
                    className="group hover:bg-slate-50/80 transition-colors cursor-pointer"
                  >
                    <td className="sticky left-0 bg-white group-hover:bg-slate-50/80 z-10 transition-colors px-6 py-4 border-r border-slate-100 min-w-[180px]">
                      <div>
                        <span className="text-sm font-black text-[#143E78] group-hover:underline">
                          {appointment.leadId?.patientName || appointment.patientName}
                        </span>
                        <div className="text-[10px] font-bold text-slate-400 font-mono mt-0.5">
                          {appointment.leadId?.patientPhone || appointment.patientPhone}
                        </div>
                        {(appointment.leadId?.patientEmail || appointment.patientEmail) && (
                          <div className="text-[10px] font-bold text-slate-400 mt-0.5 truncate">{appointment.leadId?.patientEmail || appointment.patientEmail}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 min-w-[140px]">
                      <div className="text-sm font-bold text-slate-700 capitalize">
                        {appointment.appointmentType === 'consultation' ? 'Online Consultation' : appointment.appointmentType?.replace('_', ' ')}
                      </div>
                    </td>
                    <td className="px-6 py-4 min-w-[160px]">
                      <div className="text-[11px] font-bold text-slate-800 flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[#143E78] shrink-0"></span>
                        <span className="truncate">
                          {appointment.appointmentDoctorId?.user?.firstName ? `Dr. ${appointment.appointmentDoctorId.user.firstName} ${appointment.appointmentDoctorId.user.lastName}` : 'Not Assigned'}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 min-w-[140px]">
                      <div className="text-sm font-bold text-slate-900">
                        {formatDateDisplay(appointment.appointmentDate)}
                      </div>
                      <div className="text-xs font-bold text-slate-500 mt-0.5">{formatTime12Hour(appointment.appointmentTimeSlot)}</div>
                    </td>
                    <td className="px-6 py-4 min-w-[120px]">
                      {getStatusBadge(appointment.status)}
                    </td>
                    <td className="px-6 py-4 min-w-[120px]">
                      <div className="text-sm font-bold text-slate-900">
                        ₹{appointment.finalPrice ?? '—'}
                      </div>
                    </td>
                    <td className="px-6 py-4 min-w-[120px]">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${PAYMENT_STATUS_STYLES[appointment.paymentStatus] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                        {(appointment.paymentStatus || 'pending').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-6 py-4 min-w-[120px]">
                      {appointment.payoutStatus ? (
                        <Link
                          to={`/payouts/${appointment.payoutId}`}
                          onClick={(e) => e.stopPropagation()}
                          className={`inline-flex items-center px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider border ${PAYOUT_STATUS_STYLES[appointment.payoutStatus] || 'bg-gray-100 text-gray-700 border-gray-200'}`}
                        >
                          {appointment.payoutStatus.replace(/_/g, ' ')}
                        </Link>
                      ) : (
                        <span className="text-[10px] font-black text-slate-400 uppercase">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 min-w-[120px]">
                      <div className="text-sm font-bold text-slate-900">
                        ₹{appointment.platformFee ?? 0}
                      </div>
                    </td>
                    <td className="px-6 py-4 min-w-[100px]">
                      <div className="text-[10px] font-black text-[#143E78] font-mono">
                        #{appointment.bookingId || appointment._id.slice(-8).toUpperCase()}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="9" className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-16 bg-slate-50 rounded-3xl flex items-center justify-center mb-4 border-2 border-dashed border-slate-200">
                        <Calendar size={28} className="text-slate-300" />
                      </div>
                      <p className="text-sm font-black text-slate-500 uppercase tracking-wider">No appointments found</p>
                      <p className="text-xs font-bold text-slate-400 mt-1">Try adjusting your filters or search criteria</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 py-2">
          <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
            Showing {pagination.total > 0 ? (pagination.page - 1) * pagination.limit + 1 : 0}-{Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} records
          </div>
          <div className="w-full sm:w-auto overflow-x-auto pb-2 sm:pb-0">
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.pages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {deleteId && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
          <div className="relative bg-white rounded-[28px] border border-slate-100 shadow-[0_20px_80px_-20px_rgba(20,62,120,0.25)] w-full max-w-md p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-black text-[#143E78] tracking-tight">Delete Appointment</h3>
              <button onClick={() => setDeleteId(null)} className="p-1.5 text-slate-400 hover:text-slate-600 rounded-xl hover:bg-slate-50 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-slate-600 font-medium leading-relaxed mb-6">Are you sure you want to delete this appointment? This action cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteId(null)} className="flex-1 px-4 py-3 border-2 border-slate-200 rounded-2xl text-xs font-black text-slate-600 hover:bg-slate-50 transition-colors">
                Dismiss
              </button>
              <button onClick={handleDelete} disabled={deleting} className="flex-1 px-4 py-3 bg-rose-600 text-white rounded-2xl text-xs font-black hover:bg-rose-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2">
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 size={16} />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentList;
